import * as HeroIconsOutline from '@heroicons/react/outline';
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import { Side } from '../types';
import { appendClassProps } from '../util';
import { Variant } from './index.types';
// Component for conditionally forwarding ref
const UseOverrideRef = ({ forwardedRef, buttonRef }) => {
    useImperativeHandle(forwardedRef, () => {
        return {
            ...buttonRef?.current,
            focus: () => {
                buttonRef?.current?.focus();
            },
            getBoundingClientRect: () => buttonRef?.current?.getBoundingClientRect(),
            offsetParent: buttonRef?.current?.offsetParent
        };
    });
    return null;
};
/**
 - Use button as a generic clickable component
 */
const Button = ({ disabled = false, iconSide = Side.left, variant = Variant.primary, type, children, icon, className, size, onClick }, ref) => {
    const buttonRef = useRef(null);
    let Icon;
    if (icon)
        Icon = HeroIconsOutline[icon];
    const noOp = useCallback(() => { }, []);
    const variantStyle = useMemo(() => {
        switch (variant) {
            case Variant.secondary:
                return `bg-gray-400 ${disabled
                    ? 'opacity-60 cursor-default text-gray-50'
                    : 'hover:bg-gray-500 text-white'}`;
            case Variant.secondaryFilled:
                return `bg-blue-800 ${disabled
                    ? 'opacity-60 cursor-default text-gray-50'
                    : 'hover:bg-blue-900 text-white'}`;
            case Variant.primary:
            default:
                return `bg-emerald-400 ${disabled
                    ? 'opacity-60 cursor-default text-gray-50'
                    : 'hover:bg-emerald-500 text-white'}`;
        }
    }, [disabled, variant]);
    const iconStyled = useMemo(() => icon ? (React.createElement(Icon, { "data-testid": "icon", className: `${size === 'small' ? '-mx-[3px]' : '-mx-1'} inline ${size === 'small' ? 'w-5 h-5' : 'w-6 h-6'}` })) : null, [Icon, size]);
    const minWidthClass = useMemo(() => {
        switch (size) {
            case 'small':
                return 'min-w-[1.75rem]';
            default:
                return 'min-w-[2.25rem]';
        }
    }, [size]);
    return (React.createElement(React.Fragment, null,
        ref && Object.keys(ref).length > 0 ? (React.createElement(UseOverrideRef, { forwardedRef: ref, buttonRef: buttonRef })) : null,
        React.createElement("button", { ref: buttonRef, type: type, disabled: disabled, "data-testid": "button", className: `whitespace-nowrap ${size === 'small' ? 'h-7' : 'h-9'} ${variantStyle} rounded-sm outline-none flex flex-row items-center gap-2 ${size === 'small' ? 'text-sm' : 'text-base'} ${minWidthClass} justify-center${children ? ' px-4' : ''}${appendClassProps(className)}`, onClick: onClick ? onClick : noOp },
            icon && iconSide === Side.left && iconStyled,
            children,
            icon && iconSide === Side.right && iconStyled)));
};
export * from './index.types';
export { WrappedButton as Button, Button as BareButton }; // for storybook (fails to properly parse component if wrapped with forwardRef)
const WrappedButton = forwardRef(Button);
//# sourceMappingURL=index.js.map